/**
 * GrantPage — renders an edge's sequence, and nothing else.
 *
 * The page has no opinion about which of the 22 edges it is showing. It asks
 * grantOrder for the sequence and renders the slots in that order. A slot the
 * sequence doesn't name is NOT rendered even when a caller supplies it — which
 * is what stops an edge from quietly acquiring a component the spec says it
 * doesn't have (the failure that grew the old page four escape-hatch modes).
 */
import React, { Fragment } from 'react';
import { OfselfMark } from '@ofself/shell';
// THE SHELL'S SANS, loaded here because this page never mounts <Shell>, which is
// where the shell imports it. Without this every Hanken Grotesk string on the
// grant page fell back to the system face while the dashboard, which does mount
// the shell, rendered the real one.
import '@fontsource-variable/hanken-grotesk';
import { sequenceFor } from '../../pages/authorization/grantOrder';
import './tokens.css';

/**
 * THE FRAME: a field, and one block standing on it.
 *
 * The block is the shell's own grammar — a panel with a title bar, hairline
 * seams, no radius and no shadow — sitting centred on a dotted field. It opens on
 * its figure; a title bar is drawn only when a caller has something to put in it.
 *
 * THE FIGURE sits above the sequence and outside it. Not a slot: slots are STEPS,
 * and a step absent from an edge's sequence is deliberately not rendered. The
 * figure is chrome — the bond between you and an app when the register holds the
 * app's mark, else the ofSelf S alone.
 */
export function GrantFrame({ className = '', title = null, children, ...rest }) {
  return (
    <div className="gp-field">
      <div className={`gp ${className}`.trim()} {...rest}>
        {title && <header className="gp-bar">{title}</header>}
        {children}
      </div>
    </div>
  );
}

export default function GrantPage({ edge, slots = {}, has = {}, className = '',
                                    title, figure = null }) {
  const sequence = sequenceFor(edge, has);
  return (
    <GrantFrame className={className} title={title}
                data-edge={edge} data-testid="grant-page">
      {figure || (
        <div className="gp-mast">
          <OfselfMark className="gp-mast__mark" />
        </div>
      )}
      {sequence.map((key, i) => {
        if (!slots[key]) return null;
        // THE TERMS SHARE A ROW. What it costs and how long it lasts are one kind
        // of fact, so when they arrive together they sit side by side as a pair.
        // The wrapper is layout only; the order is still the sequence's.
        if (key === 'PRICE' && sequence[i + 1] === 'C10' && slots.C10) {
          return (
            <div className="gp-terms-row" key="terms">
              {slots.PRICE}
              {slots.C10}
            </div>
          );
        }
        if (key === 'C10' && sequence[i - 1] === 'PRICE' && slots.PRICE) return null;
        return <Fragment key={key}>{slots[key]}</Fragment>;
      })}
    </GrantFrame>
  );
}

export { sequenceFor };
